import React, { useEffect, useState } from 'react';
import type { GridApi } from 'ag-grid-community';
import { withStyles } from '@material-ui/core';
import { compose } from 'recompose';
import { withUserProfile } from '../../../../../../userProfile/userProfileContext';
import { Resize } from '../../../../../../layout/layoutHeader/components';
import ShowHideColumns from '../../../../../../layout/layoutHeader/showHideColumns';
import appConstants from '@appConstants';
import ClearAllFilterButton from '@dataGrid/components/columnFilters/ClearAllFilterButton';
import useActiveGridTab from '../../common/hooks/useActiveGridTab';
import TrailerMovesGridFilter from '../Filter/GridFilter';
import { SearchBar as PickupsSearchBar } from '../../Pickups/components/PickupGridHeader/GridHeaderControls';
import type { TrailerMovesLayoutStack } from '../types';

const styles = (theme: { iconColor?: string; AppBGColor?: string }) => ({
  iconLayout: {
    float: 'left',
    color: theme.iconColor,
    '&>span': {
      padding: '10px 10px',
      color: theme.iconColor,
    },
  },
  filterContainer: {
    marginRight: 8,
    marginTop: 3,
  },
  headerIconStyles: {
    padding: '10px 10px',
    borderLeft: `1px solid ${theme.AppBGColor}`,
    cursor: 'pointer',
  },
});

interface TrailerMovesGridHeaderProps {
  classes: Record<string, string>;
  userData: { userProfile?: { columnPref?: Record<string, unknown> } };
  stack: TrailerMovesLayoutStack;
  stackIndex: number;
  headerId: string;
}

const TrailerMovesGridHeader = (props: TrailerMovesGridHeaderProps) => {
  const { classes, userData, stack, stackIndex, headerId } = props;
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const { isCurrentGrid } = useActiveGridTab({
    stack,
    stackIndex,
    gridId: appConstants.gridNames.TrailerMovesGrid,
  });

  useEffect(() => {
    const gridApiRegistrationEvent = `${headerId}_setGridApi`;
    const handleGridApiRegistered = (api: GridApi) => setGridApi(api);
    stack.layoutManager.eventHub.on(gridApiRegistrationEvent, handleGridApiRegistered);
    return () => {
      stack.layoutManager.eventHub.off(gridApiRegistrationEvent, handleGridApiRegistered);
    };
  }, [headerId, stack.layoutManager.eventHub]);

  if (!isCurrentGrid) {
    return null;
  }

  return (
    <>
      <PickupsSearchBar eventHub={stack.layoutManager.eventHub} headerId={headerId} />
      <div className={`${classes.iconLayout} ${classes.filterContainer} glHeaderIcons`}>
        <TrailerMovesGridFilter stack={stack} headerId={headerId} />
      </div>
      <ClearAllFilterButton gridApi={gridApi} />
      <div className={`${classes.headerIconStyles} ${classes.iconLayout} glHeaderIcons`}>
        <ShowHideColumns api={gridApi} userData={userData} />
      </div>
      <Resize {...props} />
    </>
  );
};

export default compose(
  withStyles(styles as Parameters<typeof withStyles>[0]),
  withUserProfile
)(TrailerMovesGridHeader as React.FC<TrailerMovesGridHeaderProps>) as React.ComponentType<TrailerMovesGridHeaderProps>;
